if (typeof VideoSliderSection !== 'function') {
  class VideoSliderSection extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
    }

    connectedCallback() {
      // Wait for DOM to be ready and css-slider to initialize
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        // Small delay to ensure css-slider has initialized
        setTimeout(() => this.init(), 100);
      }
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      this.slider = this.querySelector('[data-video-slider]');
      this.items = this.querySelectorAll('[data-video-item]');
      this.player = this.querySelector('[data-video-player]');
      this.overlay = this.querySelector('[data-video-overlay]');
      this.playerVideo = this.player?.querySelector('[data-player-video]');
      this.playerAuthor = this.player?.querySelector('[data-player-author]');
      this.playerProduct = this.player?.querySelector('[data-player-product]');
      this.variantSelectWrapper = this.playerProduct?.querySelector('[data-product-variants-select]');
      this.variantSelect = this.playerProduct?.querySelector('[data-variant-select]');
      this.closeBtn = this.player?.querySelector('[data-player-close]');
      this.prevBtn = this.player?.querySelector('[data-player-prev]');
      this.nextBtn = this.player?.querySelector('[data-player-next]');
      this.muteBtn = this.player?.querySelector('[data-player-mute]');
      
      this.sliderPrevBtn = this.querySelector('[data-slider-prev]');
      this.sliderNextBtn = this.querySelector('[data-slider-next]');
      
      this.currentIndex = -1;
      this.videos = [];
      this.isMuted = true;
      
      // Preview autoplay settings
      this.previewAutoplay = this.dataset.previewAutoplay === 'true';
      this.previewVideos = this.querySelectorAll('[data-preview-video]');
      this.currentPreviewIndex = -1;
      this.previewPaused = false;
      
      this.setupEventListeners();
      
      // Initialize preview autoplay if enabled
      if (this.previewAutoplay && this.previewVideos.length > 0) {
        this.initPreviewAutoplay();
      }

      this.checkHashDeepLink();
    }

    checkHashDeepLink() {
      const hash = window.location.hash.replace('#', '');
      if (!hash) return;

      const matchIndex = this.videos.findIndex(v => v.slideId === hash);
      if (matchIndex !== -1) {
        setTimeout(() => {
          this.scrollIntoViewport();
          this.openPlayer(matchIndex);
        }, 300);
      }
    }

    scrollIntoViewport() {
      const rect = this.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    
    setupEventListeners() {
      // Collect video data from items and add click handlers
      this.items.forEach((item, index) => {
        const data = {
          index,
          itemType: item.dataset.itemType || 'video',
          slideId: item.dataset.slideId || '',
          videoUrl: item.dataset.videoUrl || '',
          videoType: item.dataset.videoType || 'hosted',
          videoSources: item.dataset.videoSources ? JSON.parse(item.dataset.videoSources) : [],
          thumbnailUrl: item.dataset.thumbnailUrl || '',
          fullImageUrl: item.dataset.fullImageUrl || '',
          author: item.dataset.author || '',
          caption: item.dataset.caption || '',
          productId: item.dataset.productId || '',
          productTitle: item.dataset.productTitle || '',
          productPrice: item.dataset.productPrice || '',
          productImage: item.dataset.productImage || '',
          productUrl: item.dataset.productUrl || '',
          variantId: item.dataset.variantId || '',
          productVariants: item.dataset.productVariants ? JSON.parse(item.dataset.productVariants) : []
        };
        this.videos.push(data);
        
        // Click handler for each item
        item.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openPlayer(index);
        });
      });
      
      // Close button
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closePlayer();
        });
      }
      
      // Overlay click to close
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closePlayer());
      }

      // Variant select change
      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', (e) => {
          e.stopPropagation();
          this.onVariantChange(e.target.value);
        });
      }
      
      // Player navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.navigatePlayer('prev');
        });
      }
      
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.navigatePlayer('next');
        });
      }
      
      // Mute button
      if (this.muteBtn) {
        this.muteBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleMute();
        });
      }
      
      // Slider navigation
      if (this.sliderPrevBtn) {
        this.sliderPrevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.scrollSlider('prev');
        });
      }
      
      if (this.sliderNextBtn) {
        this.sliderNextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.scrollSlider('next');
        });
      }
      
      // Keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (!this.player?.classList.contains('is-open')) return;
        
        if (e.key === 'Escape') {
          this.closePlayer();
        } else if (e.key === 'ArrowLeft') {
          this.navigatePlayer('prev');
        } else if (e.key === 'ArrowRight') {
          this.navigatePlayer('next');
        }
      });
      
      // Listen for scroll on the slider holder
      const scrollContainer = this.getScrollContainer();
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', () => this.updateSliderNavigation(), { passive: true });
      }

      // Also update on window resize
      window.addEventListener('resize', () => this.updateSliderNavigation(), { passive: true });

      // Update slider navigation state - with multiple attempts to ensure css-slider is ready
      this.updateSliderNavigation();
      
      // Listen for css-slider ready event
      if (this.slider) {
        this.slider.addEventListener('ready', () => this.updateSliderNavigation());
        this.slider.addEventListener('reset', () => this.updateSliderNavigation());
      }
      
      // Fallback: update navigation after delays to catch late initialization
      setTimeout(() => this.updateSliderNavigation(), 200);
      setTimeout(() => this.updateSliderNavigation(), 500);
      setTimeout(() => this.updateSliderNavigation(), 1000);
    }

    getScrollContainer() {
      if (!this.slider) return null;
      return this.slider.querySelector('.css-slider-holder') || this.slider;
    }
    
    openPlayer(index) {
      if (index < 0 || index >= this.videos.length) return;
      
      // Stop preview autoplay when opening player
      if (this.previewAutoplay) {
        this.stopAllPreviews();
      }
      
      this.currentIndex = index;
      const video = this.videos[index];
      
      // Update active state on items
      this.items.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
      });
      
      // Update author
      if (this.playerAuthor) {
        this.playerAuthor.textContent = video.author;
      }
      
      // Load video
      this.loadVideo(video);
      
      // Update product card
      this.updateProductCard(video);
      
      // Update navigation
      this.updatePlayerNavigation();
      
      // Open player
      this.player?.classList.add('is-open');
      this.overlay?.classList.add('is-open');
      document.body.classList.add('video-player-open');
      
      // Scroll item into view
      this.scrollItemIntoView(index);
    }
    
    closePlayer() {
      this.player?.classList.remove('is-open');
      this.overlay?.classList.remove('is-open');
      document.body.classList.remove('video-player-open');
      
      // Pause and clean up video
      this.stopVideo();
      
      // Remove active state
      this.items.forEach(item => item.classList.remove('is-active'));
      
      this.currentIndex = -1;
      
      // Resume preview autoplay after closing player
      if (this.previewAutoplay && this.previewVideos.length > 0) {
        this.previewPaused = false;
        setTimeout(() => this.startNextPreview(), 300);
      }
    }
    
    showLoading() {
      this.playerVideo?.classList.add('is-loading');
    }

    hideLoading() {
      this.playerVideo?.classList.remove('is-loading');
    }

    loadVideo(videoData) {
      if (!this.playerVideo) return;
      
      // Show loading state
      this.showLoading();
      
      // Clear existing content but keep the loader
      const loader = this.playerVideo.querySelector('.video-slider__player-loader');
      this.playerVideo.innerHTML = '';
      
      // Re-add loader
      if (loader) {
        this.playerVideo.appendChild(loader);
      } else {
        const newLoader = document.createElement('div');
        newLoader.className = 'video-slider__player-loader';
        newLoader.innerHTML = '<div class="video-slider__player-loader-spinner"></div>';
        this.playerVideo.appendChild(newLoader);
      }

      const isImage = videoData.itemType === 'image';

      // Toggle mute button visibility based on item type
      if (this.muteBtn) {
        this.muteBtn.style.display = isImage ? 'none' : '';
      }

      if (isImage) {
        const img = document.createElement('img');
        img.src = videoData.fullImageUrl || videoData.thumbnailUrl;
        img.alt = videoData.author || '';
        img.className = 'video-slider__player-image';
        img.addEventListener('load', () => this.hideLoading());
        img.addEventListener('error', () => this.hideLoading());
        this.playerVideo.appendChild(img);

        setTimeout(() => this.hideLoading(), 5000);
        return;
      }
      
      if (videoData.videoType === 'youtube') {
        const videoId = this.extractYouTubeId(videoData.videoUrl);
        if (videoId) {
          const iframe = document.createElement('iframe');
          iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${this.isMuted ? 1 : 0}&playsinline=1&rel=0`;
          iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
          iframe.allowFullscreen = true;
          
          iframe.addEventListener('load', () => this.hideLoading());
          
          this.playerVideo.appendChild(iframe);
        }
      } else if (videoData.videoType === 'vimeo') {
        const videoId = this.extractVimeoId(videoData.videoUrl);
        if (videoId) {
          const iframe = document.createElement('iframe');
          iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=${this.isMuted ? 1 : 0}&playsinline=1`;
          iframe.allow = 'autoplay; fullscreen; picture-in-picture';
          iframe.allowFullscreen = true;
          
          iframe.addEventListener('load', () => this.hideLoading());
          
          this.playerVideo.appendChild(iframe);
        }
      } else {
        const video = document.createElement('video');
        video.autoplay = true;
        video.loop = true;
        video.playsInline = true;
        video.muted = this.isMuted;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        if (videoData.videoSources.length > 0) {
          videoData.videoSources.forEach(s => {
            const source = document.createElement('source');
            source.src = s.url;
            source.type = s.type;
            video.appendChild(source);
          });
        } else {
          video.src = videoData.videoUrl;
        }
        
        video.addEventListener('canplay', () => this.hideLoading());
        
        video.addEventListener('loadeddata', () => {
          video.play().catch(() => {
            video.muted = true;
            this.isMuted = true;
            this.muteBtn?.classList.add('is-muted');
            video.play();
          });
        });
        
        video.addEventListener('error', () => this.hideLoading());
        
        this.playerVideo.appendChild(video);
      }
      
      // Update mute button state
      this.muteBtn?.classList.toggle('is-muted', this.isMuted);
      
      // Fallback: hide loading after 5 seconds in case events don't fire
      setTimeout(() => this.hideLoading(), 5000);
    }
    
    stopVideo() {
      if (!this.playerVideo) return;
      
      const video = this.playerVideo.querySelector('video');
      if (video) {
        video.pause();
        video.src = '';
      }
      
      const iframe = this.playerVideo.querySelector('iframe');
      if (iframe) {
        iframe.src = '';
      }
    }
    
    updateProductCard(videoData) {
      if (!this.playerProduct) return;
      
      const hasProduct = !!videoData.productId;
      this.player?.classList.toggle('has-product', hasProduct);

      if (!hasProduct) {
        this.playerProduct.style.display = 'none';
        return;
      }
      
      this.playerProduct.style.display = 'block';
      this.currentProductVariants = videoData.productVariants || [];
      
      const imageEl = this.playerProduct.querySelector('[data-product-image]');
      const titleEl = this.playerProduct.querySelector('[data-product-title]');
      const priceEl = this.playerProduct.querySelector('[data-product-price]');
      const linkEl = this.playerProduct.querySelector('[data-product-link]');
      const variantInput = this.playerProduct.querySelector('[data-variant-id]');
      
      if (imageEl) {
        imageEl.src = videoData.productImage;
        const template = KROWN?.settings?.locales?.product_image_alt || '__PRODUCT__ foto __INDEX__';
        imageEl.alt = template
          .replace(/__PRODUCT__/g, videoData.productTitle || '')
          .replace(/__INDEX__/g, '1');
      }
      
      if (titleEl) {
        titleEl.textContent = videoData.productTitle;
      }
      
      if (priceEl) {
        priceEl.innerHTML = videoData.productPrice;
      }
      
      if (linkEl) {
        linkEl.href = videoData.productUrl;
      }
      
      if (variantInput) {
        variantInput.value = videoData.variantId;
      }

      this.populateVariantSelect(videoData);
    }

    populateVariantSelect(videoData) {
      if (!this.variantSelect || !this.variantSelectWrapper) return;

      const variants = videoData.productVariants || [];

      if (variants.length <= 1) {
        this.variantSelectWrapper.style.display = 'none';
        return;
      }

      this.variantSelect.innerHTML = '';
      // Filter out variants that are not available
      const availableVariants = variants.filter(v => v.available);
      availableVariants.forEach(v => {
        const option = document.createElement('option');
        option.value = v.id;
        option.textContent = v.title;
        option.disabled = !v.available;
        if (String(v.id) === String(videoData.variantId)) {
          option.selected = true;
        }
        this.variantSelect.appendChild(option);
      });

      this.variantSelectWrapper.style.display = '';
    }

    onVariantChange(variantId) {
      const variant = this.currentProductVariants.find(v => String(v.id) === String(variantId));
      if (!variant) return;

      const priceEl = this.playerProduct?.querySelector('[data-product-price]');
      const variantInput = this.playerProduct?.querySelector('[data-variant-id]');

      if (priceEl) priceEl.innerHTML = variant.price;
      if (variantInput) variantInput.value = variant.id;
    }
    
    navigatePlayer(direction) {
      let newIndex = this.currentIndex;
      
      if (direction === 'prev') {
        newIndex = Math.max(0, this.currentIndex - 1);
      } else {
        newIndex = Math.min(this.videos.length - 1, this.currentIndex + 1);
      }
      
      if (newIndex !== this.currentIndex) {
        // Show loading immediately and stop current video
        this.showLoading();
        this.stopVideo();
        this.openPlayer(newIndex);
      }
    }
    
    updatePlayerNavigation() {
      if (this.prevBtn) {
        this.prevBtn.classList.toggle('disabled', this.currentIndex <= 0);
      }
      if (this.nextBtn) {
        this.nextBtn.classList.toggle('disabled', this.currentIndex >= this.videos.length - 1);
      }
    }
    
    toggleMute() {
      this.isMuted = !this.isMuted;
      this.muteBtn?.classList.toggle('is-muted', this.isMuted);
      if (this.muteBtn) {
        const locales = window.KROWN?.settings?.locales || {};
        this.muteBtn.setAttribute('aria-label', this.isMuted ? (locales.unmute || 'Unmute video') : (locales.mute || 'Mute video'));
        this.muteBtn.setAttribute('aria-pressed', this.isMuted ? 'true' : 'false');
      }
      
      const video = this.playerVideo?.querySelector('video');
      if (video) {
        video.muted = this.isMuted;
      }
      
      // For iframe embeds, we'd need to reload with different params
      const iframe = this.playerVideo?.querySelector('iframe');
      if (iframe) {
        const src = iframe.src;
        if (src.includes('youtube.com')) {
          iframe.src = src.replace(/mute=[01]/, `mute=${this.isMuted ? 1 : 0}`);
        } else if (src.includes('vimeo.com')) {
          iframe.src = src.replace(/muted=[01]/, `muted=${this.isMuted ? 1 : 0}`);
        }
      }
    }
    
    scrollSlider(direction) {
      const scrollContainer = this.getScrollContainer();
      if (!scrollContainer) return;
      
      const itemWidth = this.items[0]?.offsetWidth || 220;
      const gap = 16;
      const scrollAmount = (itemWidth + gap) * 2;
      
      scrollContainer.scrollBy({
        left: direction === 'prev' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });

      // Update navigation after scroll completes
      setTimeout(() => this.updateSliderNavigation(), 350);
    }
    
    updateSliderNavigation() {
      const scrollContainer = this.getScrollContainer();
      if (!scrollContainer) return;
      
      const scrollLeft = Math.round(scrollContainer.scrollLeft);
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      
      if (this.sliderPrevBtn) {
        this.sliderPrevBtn.classList.toggle('disabled', scrollLeft <= 5);
      }
      
      if (this.sliderNextBtn) {
        this.sliderNextBtn.classList.toggle('disabled', scrollLeft >= maxScroll - 5);
      }
    }
    
    scrollItemIntoView(index) {
      const item = this.items[index];
      const scrollContainer = this.getScrollContainer();
      if (!item || !scrollContainer) return;
      
      const containerRect = scrollContainer.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      
      if (itemRect.left < containerRect.left || itemRect.right > containerRect.right) {
        const scrollLeft = item.offsetLeft - (containerRect.width / 2) + (itemRect.width / 2);
        scrollContainer.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
    
    extractYouTubeId(url) {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? match[2] : null;
    }
    
    extractVimeoId(url) {
      const regExp = /vimeo\.com\/(?:video\/)?(\d+)/;
      const match = url.match(regExp);
      return match ? match[1] : null;
    }

    // Preview Autoplay Methods
    initPreviewAutoplay() {
      // Set up ended event listeners for all preview videos
      this.previewVideos.forEach((video, index) => {
        video.addEventListener('ended', () => this.onPreviewVideoEnded(index));
        video.addEventListener('canplay', () => {
          // Mark as ready
          video.dataset.ready = 'true';
        });
      });

      // Use Intersection Observer to start/pause based on visibility
      this.setupPreviewIntersectionObserver();
      
      // Start the first preview after a short delay
      setTimeout(() => this.startNextPreview(), 500);
    }

    setupPreviewIntersectionObserver() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Resume preview if it was paused
            if (this.previewPaused && this.currentPreviewIndex >= 0) {
              this.resumeCurrentPreview();
            }
          } else {
            // Pause preview when section is not visible
            this.pauseCurrentPreview();
          }
        });
      }, { threshold: 0.3 });

      observer.observe(this);
    }

    startNextPreview() {
      // Don't start if player is open or preview is paused
      if (this.player?.classList.contains('is-open') || this.previewPaused) return;
      
      // Stop current preview
      this.stopCurrentPreview();
      
      // Find next preview video
      let nextIndex = this.currentPreviewIndex + 1;
      if (nextIndex >= this.previewVideos.length) {
        nextIndex = 0; // Loop back to start
      }
      
      this.playPreview(nextIndex);
    }

    playPreview(index) {
      if (index < 0 || index >= this.previewVideos.length) return;
      
      const video = this.previewVideos[index];
      const item = video.closest('[data-video-item]');
      
      if (!video || !item) return;
      
      this.currentPreviewIndex = index;
      
      // Add playing class
      item.classList.add('is-previewing');
      video.classList.add('is-playing');
      
      // Reset and play
      video.currentTime = 0;
      video.play().catch(() => {
        // If autoplay fails, move to next
        setTimeout(() => this.startNextPreview(), 100);
      });
    }

    stopCurrentPreview() {
      if (this.currentPreviewIndex < 0) return;
      
      const video = this.previewVideos[this.currentPreviewIndex];
      const item = video?.closest('[data-video-item]');
      
      if (video) {
        video.pause();
        video.currentTime = 0;
        video.classList.remove('is-playing');
      }
      
      if (item) {
        item.classList.remove('is-previewing');
      }
    }

    pauseCurrentPreview() {
      if (this.currentPreviewIndex < 0) return;
      
      const video = this.previewVideos[this.currentPreviewIndex];
      if (video) {
        video.pause();
      }
      this.previewPaused = true;
    }

    resumeCurrentPreview() {
      if (this.currentPreviewIndex < 0 || !this.previewPaused) return;
      
      // Don't resume if player is open
      if (this.player?.classList.contains('is-open')) return;
      
      const video = this.previewVideos[this.currentPreviewIndex];
      if (video) {
        video.play().catch(() => {});
      }
      this.previewPaused = false;
    }

    onPreviewVideoEnded(index) {
      // Move to next video when current one ends
      if (index === this.currentPreviewIndex) {
        this.startNextPreview();
      }
    }

    stopAllPreviews() {
      this.previewVideos.forEach((video, index) => {
        const item = video.closest('[data-video-item]');
        video.pause();
        video.currentTime = 0;
        video.classList.remove('is-playing');
        if (item) {
          item.classList.remove('is-previewing');
        }
      });
      this.currentPreviewIndex = -1;
    }
  }
  
  if (typeof customElements.get('video-slider-section') === 'undefined') {
    customElements.define('video-slider-section', VideoSliderSection);
  }
  
  // Shopify theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.classList.contains('mount-video-slider')) {
      const section = e.target.querySelector('video-slider-section');
      if (section) {
        section.initialized = false;
        section.init();
      }
    }
  });
}
